import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { toast } from 'sonner';
import { Send, Mail, Users, Clock, CheckCircle } from 'lucide-react';

export const MessagesPage = () => {
  const { api, user } = useAuth();
  const [messages, setMessages] = useState([]);
  const [referees, setReferees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [formData, setFormData] = useState({
    subject: '',
    content: '',
    recipient_ids: []
  });

  const fetchData = async () => {
    try {
      const [messagesRes, refereesRes] = await Promise.all([
        api().get('/messages'),
        user?.role === 'admin' ? api().get('/referees') : Promise.resolve({ data: [] })
      ]);
      setMessages(messagesRes.data);
      setReferees(refereesRes.data);
    } catch (error) {
      console.error('Failed to fetch messages:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [api, user]);

  const handleSendMessage = async () => {
    if (!formData.subject || !formData.content) {
      toast.error('Subject and content are required');
      return;
    }
    
    try {
      const response = await api().post('/messages/broadcast', formData);
      toast.success(`Message sent to ${response.data.recipients} recipients`);
      setDialogOpen(false);
      setFormData({ subject: '', content: '', recipient_ids: [] });
      fetchData();
    } catch (error) {
      toast.error('Failed to send message');
    }
  };

  const handleMarkRead = async (messageId) => {
    try {
      await api().post(`/messages/${messageId}/read`);
      fetchData();
    } catch (error) {
      console.error('Failed to mark as read:', error);
    }
  };

  const formatDate = (dateStr) => {
    return new Date(dateStr).toLocaleDateString('en-US', {
      month: 'short', day: 'numeric', year: 'numeric',
      hour: '2-digit', minute: '2-digit'
    });
  };

  const isAdmin = user?.role === 'admin';

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin w-8 h-8 border-2 border-white border-t-transparent rounded-full"></div>
      </div>
    );
  }

  return (
    <div data-testid="messages-page" className="space-y-6 animate-fade-in">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-heading font-bold uppercase tracking-wide">Messages</h1>
          <p className="text-muted-foreground mt-1">
            {isAdmin ? 'Send announcements to all referees' : 'View messages from admin'}
          </p>
        </div>
        {isAdmin && (
          <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
            <DialogTrigger asChild>
              <Button 
                data-testid="compose-message-btn"
                className="bg-white text-black hover:bg-zinc-200 font-bold uppercase tracking-wide rounded-sm"
              >
                <Send className="w-4 h-4 mr-2" />
                Broadcast Message
              </Button>
            </DialogTrigger>
            <DialogContent className="bg-zinc-900 border-zinc-800 max-w-lg">
              <DialogHeader>
                <DialogTitle className="font-heading uppercase">Send Message to All Referees</DialogTitle>
              </DialogHeader>
              <div className="space-y-4 mt-4">
                <div className="flex items-center gap-2 p-3 bg-blue-900/20 border border-blue-900 text-blue-400 rounded-sm">
                  <Users className="w-4 h-4" />
                  <span className="text-sm">This message will be sent to all {referees.length} referees</span>
                </div>
                
                <div className="space-y-2">
                  <label className="text-xs font-bold uppercase tracking-wider text-zinc-500">Subject</label>
                  <Input
                    value={formData.subject}
                    onChange={(e) => setFormData({ ...formData, subject: e.target.value })}
                    placeholder="Important Announcement"
                    data-testid="message-subject"
                    className="bg-zinc-800 border-zinc-700"
                  />
                </div>
                
                <div className="space-y-2">
                  <label className="text-xs font-bold uppercase tracking-wider text-zinc-500">Message</label>
                  <Textarea
                    value={formData.content}
                    onChange={(e) => setFormData({ ...formData, content: e.target.value })}
                    placeholder="Type your message here..."
                    data-testid="message-content"
                    className="bg-zinc-800 border-zinc-700 min-h-[150px]"
                  />
                </div>
                
                <Button
                  onClick={handleSendMessage}
                  data-testid="send-message-btn"
                  className="w-full bg-white text-black hover:bg-zinc-200 font-bold uppercase"
                >
                  <Send className="w-4 h-4 mr-2" />
                  Send to All Referees
                </Button>
              </div>
            </DialogContent>
          </Dialog>
        )}
      </div>

      {/* Messages List */}
      {messages.length === 0 ? (
        <div className="bg-zinc-900/50 border border-zinc-800 p-12 text-center">
          <Mail className="w-12 h-12 text-zinc-600 mx-auto mb-4" />
          <p className="text-zinc-500">No messages yet</p>
        </div>
      ) : (
        <div className="space-y-4">
          {messages.map((message) => {
            const isRead = message.read_by?.includes(user?.id);
            
            return (
              <div
                key={message.id}
                data-testid={`message-${message.id}`}
                onClick={() => !isRead && handleMarkRead(message.id)}
                className={`bg-zinc-900/50 border p-5 cursor-pointer transition-colors ${
                  isRead ? 'border-zinc-800 opacity-60' : 'border-blue-900 hover:border-blue-700'
                }`}
              >
                <div className="flex items-start justify-between gap-4">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-2">
                      {!isRead && (
                        <span className="w-2 h-2 bg-blue-500 rounded-full" />
                      )}
                      <h3 className="font-heading font-bold">{message.subject}</h3>
                    </div>
                    <p className="text-zinc-400 whitespace-pre-wrap">{message.content}</p>
                    <div className="flex items-center gap-4 mt-3 text-sm text-zinc-500">
                      <span className="flex items-center gap-1">
                        <Clock className="w-3 h-3" />
                        {formatDate(message.created_at)}
                      </span>
                      <span>From: {message.sender_name}</span>
                      {isAdmin && (
                        <span className="flex items-center gap-1">
                          <Users className="w-3 h-3" />
                          {message.recipient_ids?.length || 0} recipients
                        </span>
                      )}
                    </div>
                  </div>
                  {isRead && (
                    <CheckCircle className="w-5 h-5 text-emerald-400 flex-shrink-0" />
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};
